import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, post, postForm } from "./client";

/** `/api/v1/customer-orders`, as `prv.web.api.customer_orders` shapes it --
 * the reconciliation report's own JSON with the row's id. */

export interface CustomerOrder {
  id: number;
  orderNumber: string;
  status: string;
  orderDate: string | null;
  siteId: number | null;
  boqId: number | null;
  document: string | null;
  documentUrl: string | null;
  values: {
    originalPo: string | null;
    revisedPo: string | null;
    poTotal: string | null;
    boqTotal: string | null;
    invoiceTotal: string | null;
  };
  gaps: { variance: string | null; poGap: string | null; billingGap: string | null; saving: string | null };
  overAuthority: boolean;
  reconciled: boolean;
  headline: string;
  history: { at: string; action: string; by: string | null; from: string | null; to: string | null; detail: string | null }[];
  site: { id: number; name: string } | null;
  /** The bill it authorises, and the job that bill was raised from. */
  boq: { id: number; name: string; status: string; quotation_id: number | null } | null;
  notes: string | null;
}

export interface CustomerOrderRegister {
  count: number;
  overAuthority: number;
  rows: CustomerOrder[];
}

export const CUSTOMER_ORDERS_KEY = ["customer-orders"] as const;
export const customerOrderKey = (id: number) => ["customer-orders", id] as const;

export function useCustomerOrders() {
  return useQuery<CustomerOrderRegister, ApiError>({
    queryKey: CUSTOMER_ORDERS_KEY,
    queryFn: () => get<CustomerOrderRegister>("/api/v1/customer-orders"),
  });
}

export function useCustomerOrder(id: number | null) {
  return useQuery<CustomerOrder, ApiError>({
    queryKey: customerOrderKey(id ?? 0),
    queryFn: () => get<CustomerOrder>(`/api/v1/customer-orders/${id}`),
    enabled: id !== null,
  });
}

function useWrite<TVars>(write: (vars: TVars) => Promise<CustomerOrder>) {
  const client = useQueryClient();
  return useMutation<CustomerOrder, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (order) => {
      client.setQueryData(customerOrderKey(order.id), order);
      void client.invalidateQueries({ queryKey: CUSTOMER_ORDERS_KEY });
      void client.invalidateQueries({ queryKey: ["boq"] });
      // The job's Customer PO step and its queue follow the order.
      void client.invalidateQueries({ queryKey: ["quotations"] });
    },
  });
}

export function useReceiveOrder() {
  return useWrite(
    (body: { number: string; value: string; order_date: string | null; boq_id: number | null; notes: string | null }) =>
      post<CustomerOrder>("/api/v1/customer-orders", body),
  );
}

export function useReviseOrder(id: number) {
  return useWrite((body: { revised: string; reason: string | null }) =>
    post<CustomerOrder>(`/api/v1/customer-orders/${id}/revise`, body),
  );
}

export function useLinkOrder(id: number) {
  return useWrite((boqId: number) => post<CustomerOrder>(`/api/v1/customer-orders/${id}/link`, { boq_id: boqId }));
}

export function useAttachOrderDocument(id: number) {
  return useWrite((file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return postForm<CustomerOrder>(`/api/v1/customer-orders/${id}/document`, form);
  });
}
